import { useState } from 'react';
import { AI_ROLE_EXPLANATION, AI_ONE_LINER } from '../constants/aiPolicy';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';

const SECTIONS = [
  {
    id: 'community',
    title: 'Community Rules',
    body: 'No harassment, threats, hate content, doxxing, scams, fraud, or attempts to manipulate rankings. No fake accounts, automated voting, purchased votes, vote swapping, or using multiple accounts to vote. No uploading malicious files or interfering with the site, server, database, or other creators’ accounts. Users can report songs, comments, profiles, and battle entries. Admins can warn, restrict, suspend, disqualify, or ban accounts.',
  },
  {
    id: 'ownership',
    title: 'Music Ownership',
    body: 'A creator may upload only music they own or have the legal rights to use. No stolen songs, copyrighted instrumentals without permission, unauthorized samples, or impersonation. Artists retain rights to their original work; IAMMUZIK only needs limited permission to host, play, display, and promote the content through the platform.',
  },
  {
    id: 'ai',
    title: 'AI and Voice Rules',
    body: AI_ROLE_EXPLANATION +
      ' AI voice generation only performs lyrics YOU wrote — it never writes them. No AI voice cloning of a real person without that person’s written permission.',
  },
  {
    id: 'content',
    title: 'Clean vs. Explicit Content',
    body: 'Songs can be marked private, public clean, or explicit/private only. Explicit music can stay in the creator’s private library, but public promotion and public-feed placement require a clean version. IAMMUZIK can remove, hide, archive, or review music that breaks the rules.',
  },
  {
    id: 'battle',
    title: 'Battle Rules',
    body: 'The Arena is a skill-based competition, not a lottery. The intended entry fee is $50, but real money is not collected until payment processing, refund rules, identity checks, age/location eligibility, and official rules are built and reviewed. A battle requires a minimum number of verified entrants (ideally 24, with 32 the clean bracket). One eligible track per entrant. 70% judge score / 30% verified audience vote. Judges score originality, lyrics, vocal delivery, production, and overall impact.',
  },
  {
    id: 'reporting',
    title: 'Reporting',
    body: 'Users can report songs, comments, profiles, and battle entries. IAMMUZIK administrators can warn, restrict, suspend, disqualify, or ban accounts for rule violations. Content can be removed, battle entries disqualified, and promotions do not guarantee results.',
  },
  {
    id: 'privacy',
    title: 'Privacy',
    body: 'Private tracks remain private unless the creator intentionally publishes a clean version. We only collect what is needed to run the platform. More detail will be added before real account data is stored in Phase 2.',
  },
  {
    id: 'terms',
    title: 'Terms of Service',
    body: 'Users may not share accounts, sell free prize memberships, or transfer Battle Passes. Promotion means IAMMUZIK commits to actions — not guaranteed streams, followers, sales, record deals, views, or career outcomes. These terms will be expanded and formally reviewed before launch.',
  },
  {
    id: 'support',
    title: 'Contact Support',
    body: 'Send us a message and we\'ll reply by email. For in-app issues, you can also use the report tools on any song, profile, or battle entry.',
  },
];

export function SafetyPage() {
  const { currentUser } = useApp();
  const [email, setEmail] = useState(currentUser?.email ?? '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSending(true); setErr(null);
    try {
      const { ticketId, message: msg } = await api.submitSupport(email, subject || 'Support request', message);
      setSent(msg + (ticketId ? ` (Ticket #${ticketId})` : ''));
      setSubject(''); setMessage('');
    } catch (e: any) {
      setErr(e?.detail?.message || e?.message || 'Could not send. Try again.');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="grid">
      <div className="card">
        <div className="section-title" style={{ margin: 0 }}>Safety & Rules</div>
        <p className="muted" style={{ fontSize: 14 }}>
          This is the public trust page. It plainly states that content can be
          removed, battle entries can be disqualified, and promotions do not
          guarantee results.
        </p>
        <div className="flex gap-8 wrap mt-16" style={{ marginTop: 14 }}>
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">
              {s.title}
            </a>
          ))}
        </div>
      </div>

      <div className="policy-banner">
        <strong>🚫 NO AI LYRICS — EVER.</strong> {AI_ONE_LINER}
      </div>

      <div className="card" style={{ borderColor: 'rgba(36,84,245,0.45)' }}>
        <div className="section-title" style={{ color: 'var(--iam-royal-blue)' }}>AI on IAMMUZIK — what it actually does</div>
        <p style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
          “{AI_ONE_LINER}”
        </p>
        <p className="muted" style={{ fontSize: 14, marginTop: 12 }}>
          {AI_ROLE_EXPLANATION}
        </p>
        <div className="flex gap-8 wrap mt-16" style={{ marginTop: 12 }}>
          <span className="chip chip-approved">✓ AI instrumental generation</span>
          <span className="chip chip-approved">✓ AI voice (performs YOUR lyrics)</span>
          <span className="chip chip-approved">✓ AI music videos (Contract 3 / 4)</span>
          <span className="chip chip-explicit">✗ AI never writes lyrics</span>
          <span className="chip chip-explicit">✗ No real-voice cloning</span>
        </div>
      </div>

      {SECTIONS.filter((s) => s.id !== 'support').map((s) => (
        <div key={s.id} className="card" id={s.id}>
          <h3 style={{ marginBottom: 8 }}>{s.title}</h3>
          <p className="muted" style={{ fontSize: 14, margin: 0 }}>{s.body}</p>
        </div>
      ))}

      <div className="card" id="support" style={{ borderColor: 'rgba(36,84,245,0.4)' }}>
        <h3 style={{ marginBottom: 6 }}>Contact Support</h3>
        <p className="muted" style={{ fontSize: 14, margin: '0 0 14px' }}>
          Send us a message and we&rsquo;ll reply by email. For in-app issues, you can also use the report tools on any song, profile, or battle entry.
        </p>
        {sent ? (
          <div className="chip chip-approved" style={{ display: 'block', padding: 14 }}>{sent}</div>
        ) : (
          <div className="grid" style={{ gap: 10 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="label">Email</label>
              <input className="input" type="email" value={email} placeholder="you@email.com" onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="label">Subject</label>
              <input className="input" value={subject} placeholder="What&rsquo;s this about?" onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="label">Message</label>
              <textarea className="textarea" value={message} rows={4} placeholder="Tell us what&rsquo;s going on…" onChange={(e) => setMessage(e.target.value)} />
            </div>
            <div className="flex gap-8 wrap" style={{ alignItems: 'center' }}>
              <button className="btn btn-primary btn-sm" onClick={submit} disabled={sending || !email || !message}>{sending ? 'Sending…' : 'Send message'}</button>
              {err && <span className="chip chip-explicit" style={{ fontSize: 11 }}>{err}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
